import { z } from 'zod';
import { PREQUAL_REQUEST_SURFACES } from '../../schemas/prequal/contract.js';
import '../../schemas/prequal/formVariant.js';
import '../../schemas/prequal/storedResult.js';
import { PreQualDecisionBasis } from '../../types/leads/PreQualSubmission.js';
import { IS_DEALER, SVP_IDS } from './shared-fields.js';

/**
 * Operational events—backend state transitions: financing status changes,
 * order/preorder lifecycle, insurance verification, payments, title &
 * registration (TAR), and other server-side milestones. Fired server-side
 * through `ServerAnalytics`, with every call site passing an `EKHO_EVENTS`
 * object. Many double as Customer.io email triggers, which is why they carry
 * buyer/OEM contact fields and action links. Also includes the client-side
 * checkout performance metrics, grouped here as system measurements rather than
 * user interactions.
 *
 * Entries carry the clean canonical with the legacy emitted string held as
 * `wireName`—the wire emits `wireName ?? canonical` (see `getEventName`), so an
 * event can adopt its canonical with zero data discontinuity.
 *
 * Schemas cover caller-provided properties only. Backend identity/attribution
 * fields (`userId`, `ekho_sid`, marketing attribution) are injected by
 * `ServerAnalytics` per call and stay out of these schemas.
 */
// Shared property groups reused across many backend events. Marked `.optional()`
// where the field is only present under some tenant/OEM contexts or branches;
// schema validation is warn-only, so absent optional fields never block an emit.
const tenant = {
    programOwnerId: z.string(),
    programOwnerName: z.string(),
    oemId: z.string().optional(),
    oemName: z.string().optional(),
};
const buyer = { buyerId: z.string(), buyerName: z.string(), buyerEmail: z.string() };
const buyerFull = { buyerId: z.string(), buyerFullName: z.string(), buyerEmail: z.string() };
const vehicle = { purchasedVehicleId: z.string(), purchasedVehicleName: z.string() };
const svpIds = SVP_IDS;
/**
 * The identifiers every internal ops Slack alert opens with, mirroring
 * `STANDARD_SLACK_METADATA` in `NotificationTemplate.ts`.
 *
 * All optional but `programOwnerId`, because several of these alerts fire from
 * a catch block that never loaded the vehicle. Absence is expressed by omission
 * (convention §11): the Slack leg renders a missing token as the literal
 * `'N/A'` and the emitters drop it instead, so every Customer.io automation
 * binding one of these needs a `| default:`.
 */
const opsAlert = {
    programOwnerId: z.string().describe('The tenant the alert belongs to.'),
    purchasedVehicleId: z
        .string()
        .optional()
        .describe('Absent when the alert fires before the vehicle could be read.'),
    placedOrderId: z
        .string()
        .optional()
        .describe('The order id ops quotes in Slack. The canonical name here is `placedOrderId`, which is what the vehicle stores, not `event.orderId`.'),
    buyerFullName: z
        .string()
        .optional()
        .describe('Named so an ops reader knows whose order this is. The reader is not the buyer, so this is not readable off the recipient profile the way an email merge field would be.'),
    oemId: z.string().optional().describe('Absent for a dealer-owned order with no OEM program.'),
    sellerName: z
        .string()
        .optional()
        .describe('The selling provider by name, for the ops reader. Absent when the provider lookup found nothing rather than carrying the `N/A` the Slack message shows today.'),
    ...svpIds,
    ...IS_DEALER,
};
// Every `AssignmentSource` member, spelled out so the registry takes only a
// type import (the runtime `Lead` module drags notification templates into
// every bundle that loads events). The `Record` fails to compile when a member
// is added or removed without updating this list.
const LEAD_ASSIGNMENT_SOURCES = {
    round_robin: true,
    manual: true,
    reassigned: true,
    unassigned: true,
    escalation: true,
};
const OPERATIONAL_EVENTS = {
    FINANCING_UNABLE_TO_FUND: {
        canonical: 'financing_unable_to_fund',
        surface: 'backend',
        description: 'Financing could not be funded.',
        schema: z.object({
            ...tenant,
            ...buyerFull,
            ...vehicle,
            placedOrderId: z.string(),
            reason: z.string(),
            additionalNotes: z.string().optional(),
            bankName: z.string(),
            appId: z.string(),
            ...svpIds,
        }),
    },
    FINANCING_FUNDED: {
        canonical: 'financing_funded',
        surface: 'backend',
        description: "The lender approved funding of the buyer's financing packet.",
        schema: z.object({
            programOwnerId: z.string(),
            buyerId: z.string(),
            buyerFullName: z.string().optional(),
            purchasedVehicleId: z.string(),
            purchasedVehicleName: z.string(),
            placedOrderId: z.string().optional(),
            bankName: z.string(),
            appId: z.string(),
            approvedAmount: z.number(),
            ...svpIds,
        }),
    },
    FINANCING_LENDER_PAYMENT_RECEIVED: {
        canonical: 'financing_lender_payment_received',
        surface: 'backend',
        description: 'The lender funds arrived for the vehicle.',
        schema: z.object({
            programOwnerId: z.string(),
            buyerId: z.string(),
            buyerFullName: z.string().optional(),
            purchasedVehicleId: z.string(),
            purchasedVehicleName: z.string(),
            placedOrderId: z.string().optional(),
            bankName: z.string(),
            appId: z.string(),
            amount: z.number(),
            ...svpIds,
        }),
    },
    FINANCING_STIPS_RE_REQUESTED: {
        canonical: 'financing_stips_re_requested',
        surface: 'backend',
        description: 'Financing stipulations were re-requested from the buyer.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            bankName: z.string(),
            numStipsReRequested: z.number(),
            reRequestedStipsKeysArray: z.array(z.string()),
            ekhoStipsNamesString: z.string(),
            allEkhoStipsString: z.string(),
            additionalNote: z.string().optional(),
            actionLink: z.string(),
            ...svpIds,
        }),
    },
    ALL_FINANCING_STIPULATIONS_VALIDATED: {
        canonical: 'financing_all_stipulations_validated',
        wireName: 'all_financing_stipulations_validated',
        surface: 'backend',
        description: 'All financing stipulations were validated.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            bankName: z.string(),
            numStipsTotal: z.number(),
            numStipsRequiringValidation: z.number(),
            ekhoStipsKeysArray: z.array(z.string()),
            ekhoStipsRequiringValidationString: z.string(),
            allEkhoStipsString: z.string(),
            ...svpIds,
        }),
    },
    FINANCING_STIPULATION_VALIDATED: {
        canonical: 'financing_stipulation_validated',
        surface: 'backend',
        description: 'A financing stipulation was validated.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            bankName: z.string(),
            stipulation: z.unknown(),
            ...svpIds,
        }),
    },
    FINANCING_STIPULATION_EVIDENCE_PROVIDED: {
        canonical: 'financing_stipulation_evidence_provided',
        surface: 'backend',
        description: 'Buyer provided evidence for a financing stipulation.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            bankName: z.string(),
            stipulation: z.unknown(),
            ...svpIds,
        }),
    },
    LENDER_MADE_LOAN_DECISION: {
        canonical: 'financing_loan_decision_made',
        wireName: 'lender_made_loan_decision',
        surface: 'backend',
        description: 'Lender made a loan decision.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            ekhoUser: z.unknown(),
            lenderDecision: z.string(),
            apr: z.number().optional(),
            downPayment: z.number().optional(),
            loanAmount: z.number().optional(),
            loanTerm: z.number().optional(),
            dontEmailBuyer: z.boolean().optional(),
            bankName: z.string(),
            actionLink: z.string().optional(),
            firstDecision: z.boolean().optional(),
            reason: z.string().optional(),
            ...svpIds,
        }),
    },
    LENDER_CREDIT_BOX_MISS: {
        canonical: 'financing_credit_box_missed',
        wireName: 'lender_credit_box_miss',
        surface: 'backend',
        description: "Buyer fell outside the lender's credit box.",
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            ekhoUser: z.unknown(),
            bankName: z.string(),
            rejectionReasonsKeys: z.array(z.string()),
            rejectionReasons: z.array(z.string()),
            ...svpIds,
        }),
    },
    CREDIT_UNAVAILABLE: {
        canonical: 'financing_credit_unavailable',
        wireName: 'credit_unavailable',
        surface: 'backend',
        description: 'Credit was unavailable for the buyer.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            ekhoUser: z.unknown(),
            bankName: z.string(),
            rejectionReasonsKeys: z.array(z.string()),
            rejectionReasons: z.array(z.string()),
            ...svpIds,
        }),
    },
    LENDER_CREDIT_BOX_HIT: {
        canonical: 'financing_credit_box_hit',
        wireName: 'lender_credit_box_hit',
        surface: 'backend',
        description: "Buyer matched the lender's credit box.",
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            ekhoUser: z.unknown(),
            bankName: z.string(),
            ...svpIds,
        }),
    },
    CREDIT_APP_AUTO_SUBMITTED: {
        canonical: 'financing_credit_app_auto_submitted',
        wireName: 'credit_app_auto_submitted',
        surface: 'backend',
        description: 'Credit application was submitted automatically.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            placedOrderId: z.string(),
            oemEmail: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmail once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            bankName: z.string(),
            ...svpIds,
        }),
    },
    CREDIT_APP_MANUAL_SUBMISSION_REQUIRED: {
        canonical: 'financing_credit_app_manual_submission_required',
        wireName: 'credit_app_manual_submission_required',
        surface: 'backend',
        description: 'Credit application requires manual submission.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            placedOrderId: z.string(),
            oemEmail: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmail once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            bankName: z.string(),
            ...svpIds,
        }),
    },
    PURCHASED_VEHICLE_NOTE_CREATED: {
        canonical: 'vehicle_note_created',
        wireName: 'purchased_vehicle_note_created',
        surface: 'backend',
        description: 'A note was created on a purchased vehicle.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            placedOrderId: z.string(),
            oemEmail: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmail once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            oemSlackUrl: z.string().optional(),
            authorName: z.string(),
            authorEmail: z.string(),
            authorAvatarUrl: z.string().optional(),
            sendToCommChannels: z.boolean().optional(),
            dateCreated: z.unknown().optional(),
            noteContent: z.string(),
            sendToBuyer: z.boolean().optional(),
            ...svpIds,
        }),
    },
    BREX_ACH_TRANSFER_TO_OEM_PROCESSED: {
        canonical: 'payment_brex_ach_to_oem_processed',
        wireName: 'brex_ach_transfer_to_oem_processed',
        surface: 'backend',
        description: 'Brex ACH payout to the OEM was processed.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmail: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmail once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            purchasedVehicleVin: z.string().optional(),
            purchasedVehicleTotalPrice: z.number().optional(),
            achTransferAmount: z.number(),
            registrationState: z.string().optional(),
            oemExcludedEmail: z.boolean().optional(),
            ...svpIds,
        }),
    },
    BREX_ACH_TRANSFER_CREATED: {
        canonical: 'payment_brex_ach_transfer_created',
        wireName: 'brex_ach_transfer_created',
        surface: 'backend',
        description: 'Brex ACH transfer to a service recipient was created.',
        schema: z.object({
            orderId: z.string(),
            recipientId: z.string(),
            recipientName: z.string(),
            recipientVendorId: z.string().optional(),
            recipientService: z.string().optional(),
            ...buyer,
            ...vehicle,
            purchasedVehicleVin: z.string().optional(),
            purchasedVehicleTotalPrice: z.number().optional(),
            achTransferAmount: z.number(),
            registrationState: z.string().optional(),
            ...svpIds,
        }),
    },
    BUYER_MADE_DECISION_ON_NATIVE_FINANCING_OFFER: {
        canonical: 'financing_native_offer_decision_made',
        wireName: 'buyer_made_decision_on_native_financing_offer',
        surface: 'backend',
        description: 'Buyer decided on a native financing offer.',
        schema: z.object({
            orderId: z.string(),
            programOwnerId: z.string(),
            ...buyer,
            oemEmailForUpdates: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmailForUpdates once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            oemSlackUrl: z.string().optional(),
            buyerOfferDecision: z.string(),
            daysSincePurchase: z.number().optional(),
            aprOffered: z.number().optional(),
            downPaymentOffered: z.number().optional(),
            loanAmountOffered: z.number().optional(),
            monthlyPaymentOffered: z.number().optional(),
            aprRequested: z.number().optional(),
            downPaymentRequested: z.number().optional(),
            loanAmountRequested: z.number().optional(),
            loanTermRequested: z.number().optional(),
            monthlyPaymentRequested: z.number().optional(),
            orderFee: z.number().optional(),
            daysSinceLenderDecision: z.number().optional(),
            ...svpIds,
        }),
    },
    BUYER_MANUALLY_UPLOADED_INSURANCE: {
        canonical: 'insurance_manually_uploaded',
        wireName: 'buyer_manually_uploaded_insurance',
        surface: 'backend',
        description: 'Buyer manually uploaded proof of insurance.',
        schema: z.object({
            programOwnerId: z.string(),
            programOwnerName: z.string(),
            requiresSupport: z.boolean().optional(),
            ...vehicle,
            purchasedVehicleVin: z.string().optional(),
            daysSinceDatePurchased: z.number().optional(),
            orderId: z.string(),
            ...buyer,
            registrationState: z.string().optional(),
            carrierName: z.string().optional(),
            ...svpIds,
        }),
    },
    BUYER_FAILED_TO_VERIFY_INSURANCE: {
        canonical: 'insurance_verification_failed',
        wireName: 'buyer_failed_to_verify_insurance',
        surface: 'backend',
        description: "Buyer's insurance verification failed.",
        schema: z.object({
            programOwnerId: z.string(),
            requiresSupport: z.boolean().optional(),
            ...vehicle,
            purchasedVehicleVin: z.string().optional(),
            daysSinceDatePurchased: z.number().optional(),
            orderId: z.string(),
            ...buyer,
            registrationState: z.string().optional(),
            carrierName: z.string().optional(),
            numPulls: z.number().optional(),
            numGeneralErrors: z.number().optional(),
            numPolicyErrors: z.number().optional(),
            ...svpIds,
        }),
    },
    BUYER_VERIFIED_INSURANCE: {
        canonical: 'insurance_verified',
        wireName: 'buyer_verified_insurance',
        surface: 'backend',
        description: "Buyer's insurance was verified.",
        schema: z.object({
            programOwnerId: z.string(),
            ...vehicle,
            purchasedVehicleVin: z.string().optional(),
            daysSinceDatePurchased: z.number().optional(),
            orderId: z.string(),
            ...buyer,
            registrationState: z.string().optional(),
            carrierName: z.string().optional(),
            policyType: z.string().optional(),
            numPulls: z.number().optional(),
            verifier: z.string(),
            ...svpIds,
        }),
    },
    INSURANCE_DISCLOSURE_RETURNED: {
        canonical: 'insurance_disclosure_returned',
        surface: 'backend',
        description: 'A carrier disclosure was fetched and returned to the buyer at the start of the embedded quoting flow.',
        schema: z.object({
            provider: z.string(),
            riskCd: z.string().optional(),
            registrationState: z.string().optional(),
            purchasedVehicleId: z.string(),
            orderId: z.string().optional(),
            ...svpIds,
        }),
    },
    INSURANCE_QUOTE_RETURNED: {
        canonical: 'insurance_quote_returned',
        surface: 'backend',
        description: 'An embedded carrier rate quote came back and the buyer received the bridge to the carrier.',
        schema: z.object({
            provider: z.string(),
            riskCd: z.string().optional(),
            registrationState: z.string().optional(),
            purchasedVehicleId: z.string(),
            orderId: z.string().optional(),
            ...svpIds,
        }),
    },
    INSURANCE_QUOTE_FAILED: {
        canonical: 'insurance_quote_failed',
        surface: 'backend',
        description: 'An embedded carrier quote or disclosure request failed with a provider or unexpected error (excludes risk-code-not-ready and VIN-not-assigned, which hide the offer rather than fail).',
        schema: z.object({
            provider: z.string(),
            riskCd: z.string().optional(),
            registrationState: z.string().optional(),
            purchasedVehicleId: z.string(),
            orderId: z.string().optional(),
            failureReason: z.string(),
            ...svpIds,
        }),
    },
    ALL_PRODUCTS_IN_ORDER_PAID_FOR: {
        canonical: 'order_fully_paid',
        wireName: 'all_products_in_order_paid_for',
        surface: 'backend',
        description: 'All products in an order were paid for.',
        schema: z.object({
            ...tenant,
            ...buyer,
            orderId: z.string(),
            daysSincePurchase: z.number().optional(),
            ...svpIds,
        }),
    },
    PREQUAL_SUBMITTED: {
        canonical: 'financing_prequal_submitted',
        wireName: 'prequal_submitted',
        surface: 'backend',
        description: "Buyer's pre-qualification was submitted. Fired from the shared prequal service, so this is the authoritative record of every soft pull the business runs, whichever surface asked for it.",
        schema: z.object({
            programOwnerId: z.string(),
            buyerState: z.string().optional(),
            status: z.string(),
            rejectionReasons: z.array(z.string()).optional(),
            decisionBasis: z
                .enum([
                PreQualDecisionBasis.LENDER_BUY_BOX,
                PreQualDecisionBasis.SVP_FICO_FLOOR,
                PreQualDecisionBasis.FINIO,
            ])
                .optional()
                .describe('Which decision path produced this result. Absent on submissions from HTTP callers that do not honor the SP policy.'),
            // Every client surface calls one service, so without this the
            // authoritative outcome record cannot be split by where the buyer was.
            // The client-side pairs are session-joinable and this one is not, which is
            // why both exist rather than one standing in for the other.
            requestSurface: z
                .enum(PREQUAL_REQUEST_SURFACES)
                .describe('Which surface asked for the pre-qualification.'),
        }),
    },
    VEHICLE_ACH_PAYMENT_NEEDS_MANUAL_VERIFICATION: {
        canonical: 'payment_ach_needs_manual_verification',
        wireName: 'vehicle_ach_payment_needs_manual_verification',
        surface: 'backend',
        description: 'An ACH payment needs manual verification.',
        schema: z.object({
            ...tenant,
            ...buyer,
            orderId: z.string(),
            purchasedVehicleId: z.string(),
            vehicleName: z.string().optional(),
            amount: z.number(),
            paymentIntentId: z.string(),
            paymentMethod: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            vin: z.string().optional(),
            verificationUrl: z.string(),
            depositArrivalDate: z.unknown().optional(),
            depositType: z.string().optional(),
            ...svpIds,
        }),
    },
    PAYMENT_FAILED: {
        canonical: 'payment_failed',
        surface: 'backend',
        description: 'A payment failed to process after it was initiated.',
        schema: z.object({
            ...tenant,
            ...buyer,
            oemLogo: z.string().optional(),
            oemBrandColor: z.string().optional(),
            oemBrandColorLight: z.string().optional(),
            purchasedVehicleName: z.string().optional(),
            amount: z.number(),
            paymentType: z.string(),
            paymentId: z.string().optional(),
            paymentMethod: z.string().optional(),
            checkoutCartId: z.string().optional(),
            actionLink: z.string().optional(),
            orderId: z.string().optional(),
            preorderSessionId: z.string().optional(),
        }),
    },
    FULL_VEHICLE_ACH_PAYMENT_INITIATED: {
        canonical: 'payment_full_vehicle_ach_initiated',
        wireName: 'full_vehicle_ach_payment_initiated',
        surface: 'backend',
        description: 'Full-vehicle ACH payment was initiated.',
        schema: z.object({
            ...tenant,
            ...buyer,
            orderId: z.string(),
            purchasedVehicleId: z.string(),
            vehicleName: z.string().optional(),
            amount: z.number(),
            paymentIntentId: z.string(),
            paymentMethod: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            vin: z.string().optional(),
            ...svpIds,
        }),
    },
    FULL_VEHICLE_PAYMENT_RECEIVED: {
        canonical: 'payment_full_vehicle_received',
        wireName: 'full_vehicle_payment_received',
        surface: 'backend',
        description: 'Full vehicle payment was received.',
        schema: z.object({
            ...tenant,
            ...buyer,
            orderId: z.string(),
            oemEmail: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmail once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            oemLogo: z.string().optional(),
            oemBrandColor: z.string().optional(),
            oemBrandColorLight: z.string().optional(),
            purchasedVehicleId: z.string(),
            vehicleName: z.string().optional(),
            amount: z.number(),
            paymentId: z.string().optional(),
            paymentMethod: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            state: z.string().optional(),
            isInspectionRequired: z.boolean().optional(),
            isPickup: z.boolean().optional(),
            isDelivery: z.boolean().optional(),
            vin: z.string().optional(),
            actionLink: z.string().optional(),
            ...svpIds,
        }),
    },
    TAR_PACKET_UPLOADED: {
        canonical: 'tar_packet_uploaded',
        surface: 'backend',
        description: 'A title & registration packet was uploaded.',
        schema: z.object({
            programOwnerId: z.string(),
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            daysSincePurchase: z.number().optional(),
            transactionId: z.string(),
            insuranceReqEnum: z.string().optional(),
            isInsuranceCompleted: z.boolean().optional(),
            registrationState: z.string().optional(),
            tarAgent: z.string().optional(),
            ...svpIds,
        }),
    },
    TAR_TRANSACTION_STARTED: {
        canonical: 'tar_transaction_started',
        surface: 'backend',
        description: 'A title & registration transaction was started.',
        schema: z.object({
            programOwnerId: z.string(),
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            daysSincePurchase: z.number().optional(),
            transactionId: z.string(),
            registrationState: z.string().optional(),
            ...svpIds,
        }),
    },
    /**
     * Canonical keeps the historic PascalCase wire name (`VehicleStage_updated`)
     * because dealer Zapier webhooks trigger on that exact string; changing it
     * requires a coordinated data migration.
     */
    VEHICLE_STAGE_UPDATED: {
        canonical: 'vehicle_stage_updated',
        wireName: 'VehicleStage_updated',
        surface: 'backend',
        description: 'Vehicle lifecycle stage was updated.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmailForUpdates once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            stage: z.string(),
            vehiclePaymentStatus: z.string().optional(),
            purchaseTotalWithAddDues: z.number().optional(),
            purchaseTotal: z.number().optional(),
            ...svpIds,
        }),
    },
    VEHICLE_SALE_CONSUMMATED: {
        canonical: 'vehicle_sale_consummated',
        surface: 'backend',
        description: 'The vehicle sale was consummated.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmailForUpdates once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            vehiclePaymentStatus: z.string().optional(),
            purchaseTotal: z.number().optional(),
            ...svpIds,
        }),
    },
    VEHICLE_FINANCING_FINALIZED: {
        canonical: 'financing_terms_locked',
        wireName: 'vehicle_financing_finalized',
        surface: 'backend',
        description: "The buyer's final loan terms were locked to the selected lender.",
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmailForUpdates once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            vehiclePaymentStatus: z.string().optional(),
            purchaseTotal: z.number().optional(),
            ...svpIds,
        }),
    },
    BUYER_SIGNED_DOC: {
        canonical: 'vehicle_document_signed',
        wireName: 'buyer_signed_doc',
        surface: 'backend',
        description: 'Buyer e-signed a document.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmailForUpdates once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            daysUntilDelivery: z.number().optional(),
            daysSinceApproval: z.number().optional(),
            vehiclePaymentStatus: z.string().optional(),
            purchaseTotal: z.number().optional(),
            documentSetName: z.string().optional(),
            documentName: z.string(),
            sellerSignatureUrl: z.string().optional(),
            ekhoSignatureUrl: z.string().optional(),
            bankName: z.string().optional(),
            ...svpIds,
        }),
    },
    CREDIT_APPLICATION_WAITING_FOR_SUBMISSION_WINDOW: {
        canonical: 'financing_credit_app_awaiting_submission_window',
        wireName: 'credit_application_waiting_for_submission_window',
        surface: 'backend',
        description: 'Credit application is waiting for its submission window.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            numBoltOnAccessories: z.number().optional(),
            daysSinceDatePurchased: z.number().optional(),
            vin: z.string().optional(),
            vehicleStage: z.string().optional(),
            orderId: z.string(),
            amountFinanced: z.number().optional(),
            daysUntilSubmission: z.number().optional(),
            ...svpIds,
        }),
    },
    CREDIT_APPLICATION_SUBMITTED_BY_BUYER: {
        canonical: 'financing_credit_app_submitted_by_buyer',
        wireName: 'credit_application_submitted_by_buyer',
        surface: 'backend',
        description: 'Buyer submitted the credit application themselves.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            numBoltOnAccessories: z.number().optional(),
            daysSinceDatePurchased: z.number().optional(),
            vin: z.string().optional(),
            vehicleStage: z.string().optional(),
            orderId: z.string(),
            amountFinanced: z.number().optional(),
            ...svpIds,
        }),
    },
    ODOMETER_VALIDITY_SET: {
        canonical: 'vehicle_odometer_validity_set',
        wireName: 'odometer_validity_set',
        surface: 'backend',
        description: "A vehicle's odometer reading validity was set.",
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            daysSincePurchase: z.number().optional(),
            odometerValidity: z.unknown(),
            ...svpIds,
        }),
    },
    MOTORCYCLE_ENGINE_NUMBER_WAS_ASSIGNED: {
        canonical: 'vehicle_engine_number_assigned',
        wireName: 'motorcycle_engine_number_was_assigned',
        surface: 'backend',
        description: "A motorcycle's engine number was assigned.",
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            registrationState: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            motorcycleEngineNumber: z.string(),
            ...svpIds,
        }),
    },
    /** Carries the buyer's temporary portal password—flagged for the backend owner's PII audit. */
    ORDER_PRIORITIZED: {
        canonical: 'order_prioritized',
        surface: 'backend',
        description: 'An order was activated and the buyer was granted portal access.',
        schema: z.object({
            ...tenant,
            ...buyer,
            orderId: z.string(),
            datePurchased: z.unknown().optional(),
            mainImgUrl: z.string().optional(),
            estimatedDeliveryDate: z.unknown().optional(),
            oemWebsite: z.string().optional(),
            oemEmail: z.string().optional(),
            oemBrandColor: z.string().optional(),
            oemBrandColorLight: z.string().optional(),
            oemLogo: z.string().optional(),
            oemPrivacyPolicy: z.string().optional(),
            vehicleName: z.string().optional(),
            purchaseTotal: z.number().optional(),
            purchasedVehicles: z.array(z.unknown()).optional(),
            purchasedAccessories: z.array(z.unknown()).optional(),
            // Comma-joined display strings ('Name (id), …'), not arrays.
            vehicleNamesAndIds: z.string().optional(),
            accessoryNamesAndIds: z.string().optional(),
            numVehicles: z.number().optional(),
            numAccessories: z.number().optional(),
            orderFeeTotal: z.number().optional(),
            orderCreationType: z.string().optional(),
            actionLink: z.string().optional(),
            ...svpIds,
        }),
    },
    NEW_BUYER_ACCOUNT_CREATED: {
        canonical: 'account_created',
        wireName: 'new_buyer_account_created',
        surface: 'backend',
        description: 'A buyer account was created.',
        schema: z.object({
            programOwnerId: z.string(),
            accountType: z.literal('vehicle_buyer'),
            buyerFullName: z.string(),
            buyerEmail: z.string(),
            buyerState: z.string().optional(),
            buyerCity: z.string().optional(),
            buyerZip: z.string().optional(),
            ...svpIds,
        }),
    },
    MSO_WAS_SENT: {
        canonical: 'vehicle_mso_sent',
        wireName: 'mso_was_sent',
        surface: 'backend',
        description: "The manufacturer's statement of origin was sent.",
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            msoTrackingReference: z.string(),
            ...svpIds,
        }),
    },
    TEMP_TAG_UPLOADED: {
        canonical: 'tar_temp_tag_uploaded',
        wireName: 'temp_tag_uploaded',
        surface: 'backend',
        description: 'A temporary tag was uploaded.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            daysUntilDelivery: z.number().optional(),
            tarProcess: z.string().optional(),
            registrationState: z.string().optional(),
            fileUrl: z.string(),
            actionLink: z.string().optional(),
            ...svpIds,
        }),
    },
    UPLOAD_TEMP_TAG: {
        canonical: 'tar_temp_tag_upload_requested',
        wireName: 'upload_temp_tag',
        surface: 'backend',
        description: 'A temporary tag upload was requested.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            estimatedDeliveryDate: z.unknown().optional(),
            daysUntilDelivery: z.number().optional(),
            tarProcess: z.string().optional(),
            registrationState: z.string().optional(),
            stage: z.string().optional(),
            fulfillmentStatus: z.string().optional(),
            reason: z.string().optional(),
            ...svpIds,
        }),
    },
    INSURANCE_RE_REQUESTED: {
        canonical: 'insurance_re_requested',
        surface: 'backend',
        description: 'Proof of insurance was re-requested from the buyer.',
        schema: z.object({
            ...tenant,
            ...buyerFull,
            ...vehicle,
            additionalNote: z.string().optional(),
            actionLink: z.string().optional(),
            ...svpIds,
        }),
    },
    STRIPE_DISPUTE_UPDATED: {
        canonical: 'payment_stripe_dispute_changed',
        wireName: 'stripe_dispute_updated',
        surface: 'backend',
        description: 'A Stripe dispute was opened or closed.',
        schema: z.object({
            updateDescription: z.string().optional(),
            disputeId: z.string(),
            paymentIntentId: z.string().optional(),
            programOwnerId: z.string().optional(),
            oemId: z.string().optional(),
            buyerId: z.string().optional(),
            purchasedVehicleId: z.string().optional(),
            preorderId: z.string().optional(),
            paymentType: z.string().optional(),
            domainOwnerSvpId: z.string().optional(),
            sellerSvpId: z.string().optional(),
        }),
    },
    GRADER_FIRST_UNLOCK: {
        canonical: 'gr_first_unlock',
        wireName: 'grader_first_unlock',
        surface: 'backend',
        description: 'A grader report was unlocked for the first time.',
        schema: z.object({
            scanId: z.string(),
            userEmail: z.string(),
            userName: z.string(),
            userId: z.string(),
            summaryScores: z.unknown().optional(),
            emailSummary: z.unknown().optional(),
        }),
    },
    PREORDER_PRIORITIZED: {
        canonical: 'preorder_prioritized',
        surface: 'backend',
        description: 'A preorder was prioritized and the buyer was invited to complete checkout.',
        schema: z.object({
            ...tenant,
            buyerName: z.string(),
            buyerEmail: z.string(),
            checkoutUrl: z.string().optional(),
            mainImgUrl: z.string().optional(),
            oemWebsite: z.string().optional(),
            oemEmail: z.string().optional(),
            oemBrandColor: z.string().optional(),
            oemBrandColorLight: z.string().optional(),
            oemLogo: z.string().optional(),
            oemPrivacyPolicy: z.string().optional(),
            vehicleName: z
                .string()
                .optional()
                .describe('Primary vehicle display name as "Make Model Year" (e.g. "Stark Varg MX1.2 60HP 2026").'),
            vehicleIds: z.array(z.string()).optional().describe('Lean: IDs only, no full vehicle docs.'),
            vehicleNamesAndIds: z
                .string()
                .optional()
                .describe('Comma-separated "name (id)" for all vehicles.'),
            accessoryIds: z
                .array(z.string())
                .optional()
                .describe('Lean: IDs only, no full accessory docs.'),
            accessoryNamesAndIds: z
                .string()
                .optional()
                .describe('Comma-separated "countX name (id)" for accessories.'),
            numVehicles: z.number().optional(),
            numAccessories: z.number().optional(),
            estimatedDeliveryDate: z.unknown().optional(),
            preOrderTotal: z.number().optional(),
        }),
    },
    INVENTORY_CHECKOUT_CART_CREATED: {
        canonical: 'inventory_checkout_cart_created',
        surface: 'backend',
        description: 'A checkout cart was created for one inventory unit. A dealer user sending the buyer an invite from the Admin Portal sets isInvitation, and Customer.io sends the link by text, email, or both, as chosen.',
        // `programOwnerId` comes from the backend base props. The display names are
        // optional because the builder omits a name the program owner or OEM lacks.
        schema: z.object({
            programOwnerName: z
                .string()
                .optional()
                .describe('The program owner name, for the message copy.'),
            oemId: z.string().optional(),
            oemName: z.string().optional(),
            isInvitation: z
                .boolean()
                .describe('True when a dealer user created the cart to invite a buyer to checkout.'),
            sendByText: z.boolean().describe('True when the dealer user chose to send the link by text.'),
            sendByEmail: z
                .boolean()
                .describe('True when the dealer user chose to send the link by email.'),
            // Contact data is the message's recipient: the buyer has no Ekho profile yet,
            // so the automation sends to these fields. This event has no ad-fanout destination.
            buyerEmail: z.string().optional().describe('Recipient for email delivery.'),
            buyerPhone: z.string().optional().describe('Recipient for text delivery.'),
            checkoutUrl: z.string().describe('The checkout link the message carries.'),
            cartId: z.string(),
            inventoryItemId: z.string(),
            sellerSvpId: z.string().describe('The dealer that owns the unit and sent the invite.'),
            vehicleName: z
                .string()
                .optional()
                .describe('The unit as "Year Make Model", for the message copy.'),
            mainImgUrl: z.string().optional(),
        }),
    },
    MODEL_YEAR_UPDATED: {
        canonical: 'vehicle_model_year_updated',
        wireName: 'model_year_updated',
        surface: 'backend',
        description: 'Vehicle model year was changed.',
        schema: z.object({
            purchasedVehicleId: z.string(),
            oldYear: z.number(),
            newYear: z.number(),
            oldProductId: z.string().optional(),
            newProductId: z.string().optional(),
            oemId: z.string().optional(),
            placedOrderId: z.string().optional(),
            updatedBy: z.string(),
            updatedAt: z.number(),
        }),
    },
    ODOMETER_READING_SET: {
        canonical: 'vehicle_odometer_reading_set',
        wireName: 'odometer_reading_set',
        surface: 'backend',
        description: "Buyer's odometer reading was recorded.",
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            daysSincePurchase: z.number().optional(),
            odometerReading: z.unknown(),
            sendBuyerEmail: z.boolean().optional(),
            oemExcludedEmail: z.boolean().optional(),
            ...svpIds,
        }),
    },
    VIN_WAS_ASSIGNED: {
        canonical: 'vehicle_vin_assigned',
        wireName: 'vin_was_assigned',
        surface: 'backend',
        description: 'A VIN was assigned to a purchased vehicle.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemLogo: z.string().optional(),
            oemBrandColor: z.string().optional(),
            oemBrandColorLight: z.string().optional(),
            oemEmailForUpdates: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmailForUpdates once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            vin: z.string(),
            oemExcludedEmail: z.boolean().optional(),
            ...svpIds,
        }),
    },
    VIN_ASSIGNMENT_FAILED: {
        canonical: 'vehicle_vin_assignment_failed',
        wireName: 'vin_assignment_failed',
        surface: 'backend',
        description: 'VIN assignment failed for a purchased vehicle.',
        schema: z.object({
            programOwnerId: z.string(),
            programOwnerName: z.string(),
            purchasedVehicleId: z.string(),
            vin: z.string(),
            ...svpIds,
        }),
    },
    ORDER_PLACED_SUCCESSFULLY: {
        canonical: 'order_placed',
        wireName: 'order_placed_successfully',
        surface: 'backend',
        description: "A buyer's order was placed successfully.",
        schema: z.object({
            ...tenant,
            ...buyer,
            buyerPhoneNumber: z.string().optional(),
            orderId: z.string(),
            datePurchased: z.unknown().optional(),
            mainImgUrl: z.string().optional(),
            oemWebsite: z.string().optional(),
            oemLogo: z.string().optional(),
            oemBrandColor: z.string().optional(),
            oemBrandColorLight: z.string().optional(),
            oemEmailForUpdates: z.string().optional(),
            oemEmailForSupport: z.string().optional(),
            orderFeeTotal: z.number().optional(),
            // Delivery + pickup fees for the order. Reported alongside `purchaseTotal`,
            // which excludes them, so consumers can present them as their own line.
            fulfillmentFeeTotal: z.number().optional(),
            preOrderTotal: z.number().optional(),
            shippingAddress: z.unknown().optional(),
            pickUpLocation: z.unknown().optional(),
            paymentMethod: z.string().optional(),
            ecommSource: z.string().optional(),
            purchaseTotal: z.number().optional(),
            dueTodayTotal: z.number().optional(),
            purchasedVehicles: z.array(z.unknown()).optional(),
            purchasedAccessories: z.array(z.unknown()).optional(),
            // Comma-joined display strings ('Name (id), …'), not arrays.
            vehicleNamesAndIds: z.string().optional(),
            accessoryNamesAndIds: z.string().optional(),
            numVehicles: z.number().optional(),
            numAccessories: z.number().optional(),
            orderCreationType: z.string().optional(),
            paymentSelectionAtCheckout: z.string().optional(),
            preQualOutcome: z.string().optional(),
            oemExcludedEmail: z.boolean().optional(),
            actionLink: z.string().optional(),
            outOfState: z.boolean().optional(),
            sellerState: z.string().optional(),
            ...svpIds,
        }),
    },
    ORDER_CANCELED: {
        canonical: 'order_canceled',
        surface: 'backend',
        description: 'A purchased vehicle was canceled.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            vin: z.string().optional(),
            cancelationAgent: z.string(),
            ...svpIds,
        }),
    },
    PREORDER_PLACED_SUCCESSFULLY: {
        canonical: 'preorder_placed',
        wireName: 'preorder_placed_successfully',
        surface: 'backend',
        description: "A buyer's preorder was placed successfully.",
        schema: z.object({
            ...tenant,
            ...buyer,
            buyerPhoneNumber: z.string().optional(),
            orderId: z.string(),
            datePurchased: z.unknown().optional(),
            mainImgUrl: z.string().optional(),
            oemWebsite: z.string().optional(),
            oemLogo: z.string().optional(),
            oemBrandColor: z.string().optional(),
            oemBrandColorLight: z.string().optional(),
            oemEmailForUpdates: z.string().optional(),
            oemEmailForSupport: z.string().optional(),
            paymentMethod: z.string().optional(),
            dueTodayTotal: z.number().optional(),
            purchaseTotal: z.number().optional(),
            preorderIds: z
                .array(z.string())
                .optional()
                .describe('Lean: IDs only, no full preorder docs.'),
            vehicleName: z
                .string()
                .optional()
                .describe('Primary vehicle display name as "Make Model Year" (e.g. "Stark Varg MX1.2 60HP 2026").'),
            vehicleNamesAndIds: z
                .string()
                .optional()
                .describe('Comma-separated "name (id)" for all vehicles.'),
            numPreorders: z.number().optional(),
            accessoryIds: z
                .array(z.string())
                .optional()
                .describe('Lean: IDs only, no full accessory docs.'),
            accessoryNamesAndIds: z
                .string()
                .optional()
                .describe('Comma-separated "countX name (id)" for accessories.'),
            numberOfAccessories: z.number().optional(),
            oemExcludedEmail: z.boolean().optional(),
            estimatedRetailPriceTotal: z.number().optional(),
            ...svpIds,
        }),
    },
    FULFILLMENT_STATUS_WAS_UPDATED: {
        canonical: 'order_fulfillment_status_updated',
        wireName: 'fulfillment_status_was_updated',
        surface: 'backend',
        description: 'Fulfillment status changed for a purchased vehicle.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            paymentSelection: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            vehicleFulfillmentStatus: z.string().optional(),
            estimatedDeliveryDate: z.unknown().optional(),
            vin: z.string().optional(),
            fulfillmentDetails: z.unknown().optional(),
            vehiclePaymentStatus: z.string().optional(),
            purchaseTotalWithAddDues: z.number().optional(),
            purchaseTotal: z.number().optional(),
            oemExcludedEmail: z.boolean().optional(),
            actionLink: z.string().optional(),
            cancelationAgent: z.string().optional(),
            servicesProviderEmailForUpdates: z.string().optional(),
            servicesProviderName: z.string().optional(),
            noEmail: z.boolean().optional(),
            tarProcess: z.string().optional(),
            tarAgent: z.string().optional(),
            tempTagRequested: z.boolean().optional(),
            whiteGloveDealerName: z
                .string()
                .optional()
                .describe('Last-leg provider name when last_leg_delivery is assigned and that provider is not the program owner. Omitted otherwise so Customer.io can filter Kick Off Fulfillment white-glove emails on this property being set.'),
            ...svpIds,
        }),
    },
    PREORDER_STATUS_WAS_UPDATED: {
        canonical: 'preorder_status_updated',
        wireName: 'preorder_status_was_updated',
        surface: 'backend',
        description: "A preorder's status changed.",
        schema: z.object({
            ...tenant,
            ...buyer,
            preorderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmailForUpdates once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            vehicleName: z
                .string()
                .optional()
                .describe('Vehicle display name as "Make Model Year" (e.g. "Stark Varg MX1.2 60HP 2026").'),
            preorderName: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            status: z.string().optional(),
            oemExcludedEmail: z.boolean().optional(),
        }),
    },
    VEHICLE_WAS_REFUNDED: {
        canonical: 'vehicle_refunded',
        wireName: 'vehicle_was_refunded',
        surface: 'backend',
        description: 'A purchased vehicle was refunded.',
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            paymentSelection: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            totalRefundedAmount: z.number().optional(),
            oemExcludedEmail: z.boolean().optional(),
            typeOfRefund: z.string().optional(),
            notes: z.string().optional(),
            ...svpIds,
        }),
    },
    PREORDER_WAS_REFUNDED: {
        canonical: 'preorder_refunded',
        wireName: 'preorder_was_refunded',
        surface: 'backend',
        description: 'A preorder was refunded.',
        schema: z.object({
            ...tenant,
            ...buyer,
            preorderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            // TODO(customer-io-cutover): drop oemEmailForUpdates once Customer.io reads this.
            programOwnerEmail: z.string().optional(),
            vehicleName: z
                .string()
                .optional()
                .describe('Vehicle display name as "Make Model Year" (e.g. "Stark Varg MX1.2 60HP 2026").'),
            name: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            totalRefundedAmount: z.number().optional(),
            oemExcludedEmail: z.boolean().optional(),
            typeOfRefund: z.string().optional(),
            notes: z.string().optional(),
        }),
    },
    ESTIMATED_DELIVERY_DATE_CHANGED: {
        canonical: 'order_estimated_delivery_date_changed',
        wireName: 'estimated_delivery_date_changed',
        surface: 'backend',
        description: "A vehicle's estimated delivery date changed.",
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            estimatedDeliveryDate: z.unknown().optional(),
            previousDate: z.unknown().optional(),
            delta: z.number().optional(),
            oemExcludedEmail: z.boolean().optional(),
            actionLink: z.string().optional(),
            ...svpIds,
        }),
    },
    ESTIMATED_PRODUCTION_DATE_CHANGED: {
        canonical: 'order_estimated_production_date_changed',
        wireName: 'estimated_production_date_changed',
        surface: 'backend',
        description: "A vehicle's estimated production date changed.",
        schema: z.object({
            ...tenant,
            ...buyer,
            ...vehicle,
            orderId: z.string(),
            oemEmailForUpdates: z.string().optional(),
            daysSincePurchase: z.number().optional(),
            estimatedProductionDate: z.unknown().optional(),
            previousDate: z.unknown().optional(),
            delta: z.number().optional(),
            oemExcludedEmail: z.boolean().optional(),
            actionLink: z.string().optional(),
            ...svpIds,
        }),
    },
    REENGAGEMENT: {
        canonical: 'lead_reengagement_sent',
        wireName: 'reengagement',
        surface: 'backend',
        description: 'A re-engagement touch was sent to the buyer.',
        schema: z.object({
            cartUrl: z.string().optional(),
            cartId: z.string(),
            mainImgUrl: z.string().optional(),
            mainVehicleName: z.string().optional(),
            oemId: z.string().optional(),
            programOwnerId: z.string(),
            programOwnerName: z.string().optional(),
            oemWebsite: z.string().optional(),
            oemBrandColor: z.string().optional(),
            oemBrandColorLight: z.string().optional(),
            oemLogo: z.string().optional(),
            oemEmailForUpdates: z.string().optional(),
            oemEmailForSupport: z.string().optional(),
            buyerName: z.string().nullable().optional(),
            buyerEmail: z.string().nullable().optional(),
            selectedPaymentOption: z.string().optional(),
            vehiclesInCart: z.array(z.unknown()).optional(),
            accessoriesInCart: z.array(z.unknown()).optional(),
            // Comma-joined display strings ('Name (id), …'), not arrays.
            vehicleNamesAndIds: z.string().optional(),
            accessoryNamesAndIds: z.string().optional(),
            numVehicles: z.number().optional(),
            numAccessories: z.number().optional(),
            reEngagementMedium: z.string().optional(),
            reEngagementId: z.string().optional(),
            reEngagementDiscount: z.unknown().optional(),
            reEngagementGroup: z.string().optional(),
            orderCreationType: z.string().optional(),
            oemExcludedEmail: z.boolean().optional(),
            financingAvailable: z.boolean().optional(),
            ...svpIds,
        }),
    },
    NEW_SERVICES_PROVIDER_CREATED: {
        canonical: 'provider_created',
        wireName: 'new_services_provider_created',
        surface: 'backend',
        description: 'A new services provider account was created.',
        schema: z.object({
            servicesProviderId: z.string(),
            account_type: z.literal('services_provider'),
            name: z.string(),
            email: z.string(),
            state: z.string().optional(),
            city: z.string().optional(),
            zip: z.string().optional(),
        }),
    },
    SERVICES_PROVIDER_UPDATED: {
        canonical: 'provider_updated',
        wireName: 'services_provider_updated',
        surface: 'backend',
        description: 'A services provider account was updated.',
        schema: z.object({
            servicesProviderId: z.string(),
            account_type: z.literal('services_provider'),
            name: z.string(),
            email: z.string(),
            state: z.string().optional(),
            city: z.string().optional(),
            zip: z.string().optional(),
        }),
    },
    // -------------------------------------------------------------------------
    // Dealer invitation lifecycle. Both emit against the invitee's own profile,
    // keyed by the Firebase uid the invite flow mints — `identifyInvitee` writes
    // it just before the created event, because no `Users/{uid}` document exists
    // until the invite is accepted.
    //
    // The pair is what drives one journey: created opens it, claimed closes it.
    // Shipping created alone leaves the automation's conditional wait to time
    // out on everyone, so every invitee gets the follow-up reminder including
    // the ones who already signed in.
    // -------------------------------------------------------------------------
    INVITATION_CREATED: {
        canonical: 'invitation_created',
        surface: 'backend',
        description: 'A dealer teammate was invited to the admin portal.',
        schema: z.object({
            invitationId: z.string(),
            actionUrl: z
                .string()
                .describe("Single-use setup link. It carries the invite key, which is also set as the invitee's Firebase password until they complete setup, so this is a credential with a 7-day life and not a plain URL."),
            invitedBy: z
                .string()
                .describe("Display name of the teammate who sent the invite, resolved from Auth. The email copy addresses the invitee with this, so it is deliberately not the raw uid the invitation stores — falls back to 'Your team' when the lookup fails."),
            invitedByUserId: z.string().describe('Firebase uid behind `invitedBy`, for joining back.'),
            accountName: z
                .string()
                .optional()
                .describe('Dealership name. Absent when the services-provider read failed at invite time; the Customer.io template renders its own default.'),
            programOwnerId: z
                .string()
                .optional()
                .describe('Selects the brand logo and name the invite email is styled with. Passed explicitly because nothing upstream defaults it, and absent where the inviting flow never resolved one.'),
            servicesProviderId: z.string().optional(),
            invitationType: z.string(),
            invitationEmail: z
                .string()
                .describe("The invitee's own email address, the destination the invite was sent to. A personal address carried on this CDP-only internal ops event so the Customer.io Slack template can render it; never fanned out to an ad platform, and this entry declares no destinations."),
            invitationExpires: z
                .string()
                .describe('When the invite lapses, as an ISO 8601 timestamp, 7 days after it was created. Matches the value the notification metadata renders so the Slack template reads the same across both.'),
            invitationStatus: z
                .enum(['pending', 'accepted', 'expired'])
                .describe('Invitation lifecycle state at emit time. Always `pending` for a fresh invite.'),
            role: z
                .string()
                .optional()
                .describe('Membership role granted on acceptance. Absent when the invite left it to default.'),
        }),
    },
    INVITATION_CLAIMED: {
        canonical: 'invitation_claimed',
        surface: 'backend',
        description: 'An invitee accepted their invitation and the membership it described was granted.',
        schema: z.object({
            invitationId: z.string(),
            hoursToClaim: z
                .number()
                .describe('Hours between the invite being created and accepted. Invites expire at 168.'),
            // Optional against a `backend` base that declares it required, for the
            // same reason as on `INVITATION_CREATED`: an invitation stores '' where
            // the inviting flow never resolved one, and the emitter omits it rather
            // than ship an empty string that reads like a real id.
            programOwnerId: z.string().optional(),
            servicesProviderId: z
                .string()
                .optional()
                .describe('Read off the same field `INVITATION_CREATED` reads, so a created/claimed pair joins. Not `domainsServicesProviderId`, which is a separate field defaulting to empty.'),
            invitationType: z.string(),
            role: z.string().optional(),
        }),
    },
    // -------------------------------------------------------------------------
    // Lead record lifecycle. Fired when a Leads document is first written.
    // Attributed to the dealer SVP profile so a Customer.io journey can notify
    // the dealership. CDP-only — do not add `destinations`. Deduped by
    // `cdpLeadCreatedAt` so a retried duplicate create still emits if the first
    // attempt never reached the CDP. Buyer contact rides the payload under
    // convention 9 (stated need: the dealer acting on the lead).
    // `programOwnerId` is passed at the call site (server analytics does not
    // inject backend base props) but is not redeclared here — the surface base
    // schema already documents it.
    // -------------------------------------------------------------------------
    LEAD_CREATED: {
        canonical: 'lead_created',
        surface: 'backend',
        description: 'A Leads document was written. Emitted from createLead after persist and routing. Deduped per lead by cdpLeadCreatedAt.',
        schema: z.object({
            leadId: z.string().describe('The Leads document id.'),
            leadUrl: z.string().describe('Admin Portal deep link to the lead overview.'),
            leadDisplayName: z
                .string()
                .describe('Display name of the lead (full name, else email, else a generic label).'),
            dealerName: z
                .string()
                .optional()
                .describe('Dealer display name. Absent when neither the lead nor the SP carries a name.'),
            leadType: z.string().describe('Lead type as spaced title case (e.g. Abandoned Cart).'),
            leadVehicle: z
                .string()
                .optional()
                .describe('Vehicle of interest. Absent when no source recorded one.'),
            leadOriginChannel: z
                .string()
                .optional()
                .describe('How the lead arrived, as a human-readable origin-channel label. Absent when unset.'),
            leadEmail: z.string().optional().describe("The lead's email. Absent when not captured."),
            leadPhone: z.string().optional().describe("The lead's phone. Absent when not captured."),
            leadSource: z
                .string()
                .optional()
                .describe('First source-type label on the lead. Absent when the lead has no sources.'),
            dealerSvpId: z.string().describe('The dealer services provider this lead belongs to.'),
            stage: z.string().describe('CRM pipeline stage at creation, after routing.'),
            pipeline: z.string().describe('Which CRM pipeline the lead was created on.'),
            assignedToUserId: z
                .string()
                .optional()
                .describe('The assignee after routing. Absent when routing left the lead unassigned.'),
        }),
    },
    // -------------------------------------------------------------------------
    // Lead assignment. Fired when a lead gains a new assignee, from every
    // writer of `assignedToUserId`: round-robin routing at create, manual
    // (re)assignment in the Admin Portal, and escalation reassignment. All
    // three publish through `publishLeadUpdated`, which diffs the before and
    // after snapshots, so an unchanged or cleared assignee emits nothing.
    // Attributed to the assignee's platform-user profile (Firebase uid) so a
    // Customer.io journey can email the rep directly, the same profile the
    // escalation remind notice targets. CDP-only; buyer contact rides the
    // payload under convention 9 (stated need: the rep acting on the lead).
    // -------------------------------------------------------------------------
    LEAD_ASSIGNED: {
        canonical: 'lead_assigned',
        surface: 'backend',
        description: 'A lead gained a new assignee (routing, manual assignment, or escalation reassignment). Attributed to the assignee. Not emitted on unassign or when the assignee is unchanged.',
        schema: z.object({
            leadId: z.string().describe('The Leads document id.'),
            leadUrl: z.string().describe('Admin Portal deep link to the lead overview.'),
            leadDisplayName: z
                .string()
                .describe('Display name of the lead (full name, else email, else a generic label).'),
            dealerName: z
                .string()
                .optional()
                .describe('Dealer display name. Absent when neither the lead nor the SP carries a name.'),
            leadType: z.string().describe('Lead type as spaced title case (e.g. Abandoned Cart).'),
            leadVehicle: z
                .string()
                .optional()
                .describe('Vehicle of interest. Absent when no source recorded one.'),
            leadOriginChannel: z
                .string()
                .optional()
                .describe('How the lead arrived, as a human-readable origin-channel label. Absent when unset.'),
            leadEmail: z.string().optional().describe("The lead's email. Absent when not captured."),
            leadPhone: z.string().optional().describe("The lead's phone. Absent when not captured."),
            leadSource: z
                .string()
                .optional()
                .describe('First source-type label on the lead. Absent when the lead has no sources.'),
            dealerSvpId: z.string().describe('The dealer services provider this lead belongs to.'),
            stage: z.string().describe('CRM pipeline stage at assignment.'),
            pipeline: z.string().describe('Which CRM pipeline the lead is on.'),
            assignedToUserId: z
                .string()
                .describe('Firebase uid of the new assignee; also the profile the event lands on.'),
            previousAssignedToUserId: z
                .string()
                .optional()
                .describe('Firebase uid of the prior assignee. Absent on a first assignment.'),
            assignmentSource: z
                .enum(Object.keys(LEAD_ASSIGNMENT_SOURCES))
                .optional()
                .describe('How the assignment happened: round_robin, manual, reassigned, or escalation. Absent on legacy writes that did not stamp it.'),
        }),
    },
    // -------------------------------------------------------------------------
    // OEM Sales Agent connect follow-through. Fired when executeDealerHandoff
    // finishes lead processing after connect_buyer_to_dealer, and only when that
    // processing returns a lead for the connected dealer. That is the moment the
    // agent finished connecting the buyer to a rooftop with phone on file,
    // whether that write created or updated the dealer Lead. Not gated on lead
    // create: usual OEM flow creates the lead at phone capture; handoff often
    // updates. Attributed to the dealer SVP profile. `recipient` is the dealer
    // contact email for Customer.io To (no mailbox-profile sync). CDP-only.
    // Buyer contact rides under convention 9 (stated need: the inquiry email).
    // `programOwnerId` is passed at the call site (server analytics does not
    // inject backend base props) but is not redeclared here. The surface base
    // schema already documents it. Emitted at most once per connect: claimed on
    // the conversation before Segment track.
    // -------------------------------------------------------------------------
    LEAD_DEALER_HANDOFF_COMPLETED: {
        canonical: 'lead_dealer_handoff_completed',
        surface: 'backend',
        description: 'OEM Sales Agent connect handoff follow-through finished after lead processing returned a lead for the connected dealer SVP. Emitted at most once per connect. Attributed to the dealer. Drives the unsigned-dealer inquiry email in Customer.io when salesAgentFulfillmentEnabled is false.',
        schema: z.object({
            recipient: z
                .string()
                .optional()
                .describe('Dealer contact email for Customer.io To. Absent when connect did not resolve a dealer email.'),
            salesAgentFulfillmentEnabled: z
                .boolean()
                .describe('Whether the connected dealer has Sales Agent fulfillment (same as bdcEnabled at connect). CIO forks: false sends the unsigned inquiry email; true exits unless a second email is added later.'),
            brandName: z
                .string()
                .optional()
                .describe('OEM brand name for the inquiry subject and upsell copy. Absent when unset.'),
            buyerName: z
                .string()
                .optional()
                .describe("Buyer's display name for the inquiry email. Absent when not captured."),
            buyerPhone: z.string().describe("Buyer's phone. Required to reach handoff lead processing."),
            buyerEmail: z.string().optional().describe("Buyer's email. Absent when not captured."),
            conversationId: z.string().describe('Conversation that ran connect_buyer_to_dealer.'),
            dealerSvpId: z.string().describe('Services provider id of the connected dealer rooftop.'),
        }),
    },
    // -------------------------------------------------------------------------
    // Internal ops alerts with no action item behind them. Each is emitted from
    // the call site that handled the moment, because nothing else observes it:
    // the only code that knows the thing happened is the line that handled it.
    //
    // The reader is an Ekho ops person, not the buyer, so the buyer's name rides
    // the payload rather than being read off a recipient profile: permitted
    // under the PII tiering in `analytics-event-conventions.md` for a CDP-only
    // event with a stated need, and none of these declares a `destinations`
    // fan-out.
    //
    // No `wireName`: nothing listens on these yet, so the canonical is what the
    // Customer.io automations are authored against.
    // -------------------------------------------------------------------------
    REQUEST_DL_MATCHING_REGISTRATION_STATE: {
        canonical: 'request_dl_matching_registration_state',
        surface: 'backend',
        description: "The buyer's verified driver licence was issued by a different state than the one the vehicle registers in, which the state requires to match.",
        schema: z.object({
            ...opsAlert,
            registrationState: z.string().describe('The state the vehicle registers in.'),
            dlState: z
                .string()
                .optional()
                .describe('The state on the verified licence. Absent when the identity document carried no address, which is the case the Slack message renders as `N/A`.'),
        }),
    },
    VERIFY_STRIPE_VERIFICATION_SESSION: {
        canonical: 'verify_stripe_verification_session',
        surface: 'backend',
        description: 'Stripe Identity flagged a verification session as possible fraud, so a human has to review it before the order proceeds.',
        schema: z.object({
            ...opsAlert,
            verificationSessionId: z.string().describe('The Stripe Identity session to open for review.'),
            registrationState: z
                .string()
                .optional()
                .describe('Read from the session metadata, which the starting flow wrote.'),
        }),
    },
    // Not an `opsAlert`: the session names no order, vehicle, or tenant, so none
    // of those fields (nor `isDealer`) can resolve. The Slack automation posts to
    // one fixed channel, so it has no dealer/OEM routing to do.
    UNLINKED_IDENTITY_VERIFICATION_SESSION_CREATED: {
        canonical: 'unlinked_identity_verification_session_created',
        surface: 'backend',
        description: 'An identity verification session was created in this environment without the metadata Ekho needs to link it to an order (`env` and `order_id`), almost always because ops made it by hand in the Stripe dashboard. Ekho will not record its result until ops fixes the metadata. Fires once per session, on its created webhook; tracked on the Ekho ops profile.',
        schema: z.object({
            verificationSessionId: z
                .string()
                .describe('The Stripe Identity session ops has to fix, as the Slack post quotes it.'),
            eventType: z
                .literal('identity.verification_session.created')
                .describe('The Stripe webhook event that raised the alert. Only the created event does.'),
            missingKeys: z
                .array(z.enum(['env', 'order_id']))
                .min(1)
                .describe('Which required metadata keys are absent or blank. Only these two known names, never the keys the session actually carries, which are hand-typed free text.'),
            stripeDashboardUrl: z
                .string()
                .describe("Deep link to the session in Stripe's dashboard, built from the session id so a replayed payload still resolves."),
            env: z
                .string()
                .describe('The Ekho environment that received the webhook (`CURR_ENV`), so a staging test can be told apart from production.'),
        }),
    },
    RON_SESSION_COMPLETED: {
        canonical: 'ron_session_completed',
        surface: 'backend',
        description: 'A remote online notarization session finished and its documents were stored.',
        schema: z.object({
            ...opsAlert,
            sessionType: z.string().describe('Which party notarized: buyer or seller.'),
            documentsNotarized: z
                .number()
                .describe('How many documents came back notarized. Zero is a real outcome, not an absence.'),
            downloadLinks: z
                .string()
                .optional()
                .describe('Slack-formatted links to the notarized documents, one per form. These are seven-day GCS V4 signed URLs, so they are bearer credentials to the documents and stay in the profile timeline after they expire. Kept deliberately: ops acts on them within minutes.'),
        }),
    },
    RON_SESSION_FAILED: {
        canonical: 'ron_session_failed',
        surface: 'backend',
        description: 'A remote online notarization session was canceled before the documents signed.',
        schema: z.object({
            ...opsAlert,
            sessionType: z.string().describe('Which party was notarizing: buyer or seller.'),
            failureReason: z
                .string()
                .optional()
                .describe("The vendor's cancel code and message, joined."),
        }),
    },
    AUTO_TAR_SUCCESSFULLY_COMPLETED: {
        canonical: 'auto_tar_successfully_completed',
        surface: 'backend',
        description: 'Titling and registration paperwork was prepared automatically, with no ops step.',
        schema: z.object({
            ...opsAlert,
            registrationState: z.string().describe('The state whose forms were selected.'),
            workflow: z
                .string()
                .describe("How the buyer signs: the prep result's workflow, or 'unknown' when it returned none."),
            formsSelected: z
                .array(z.string())
                .describe('The additional state forms the selector chose, by key. An empty array means none were needed, which is what the Slack message renders as `None`.'),
            uploadsSelected: z
                .array(z.string())
                .describe('The buyer uploads the selector asked for. Empty means none were needed.'),
        }),
    },
    PERFORM_MANUAL_INTERVENTION_FOR_FAILED_AUTO_TAR: {
        canonical: 'perform_manual_intervention_for_failed_auto_tar',
        surface: 'backend',
        description: 'Automated titling paperwork preparation failed, so ops has to prepare it by hand.',
        schema: z.object({
            ...opsAlert,
            errorMessage: z
                .string()
                .describe('Why the automation failed, truncated to 300 characters by the call site.'),
            resetStatus: z
                .string()
                .optional()
                .describe("Whether the automation state was reset for a manual retry: 'success' or 'failed'. Absent where the call site had nothing to report."),
            resetMessage: z.string().optional().describe('What the reset attempt did, in words.'),
            registrationState: z
                .string()
                .optional()
                .describe('Absent when the failure happened before the vehicle could be read.'),
        }),
    },
    CG_LIENHOLDER_REBUILD_ATTEMPTED: {
        canonical: 'cg_lienholder_rebuild_attempted',
        surface: 'backend',
        description: 'An order titled through Cardinal Gray left native financing after its CG transaction was created, so that transaction may still name the lender as lienholder. The system tried once to replace it with a transaction built from the order as it stands, and `outcome` says how that went.',
        schema: z.object({
            ...opsAlert,
            outcome: z
                .enum(['rebuilt', 'failed', 'skipped'])
                .describe('rebuilt: Cardinal Gray accepted a replacement and the order now points at it. failed: Cardinal Gray rejected the replacement and the order is unchanged. skipped: no replacement was requested, and `skipReason` says why.'),
            skipReason: z
                .enum(['paperwork_prepared', 'transaction_not_completed'])
                .optional()
                .describe('Present only when `outcome` is skipped. paperwork_prepared: titling paperwork was already built from the old transaction (prep ran, a packet was set up to ship, the e-sign envelope exists, or a robotic or notarized signing started), so it has to be corrected by hand. transaction_not_completed: the old transaction was still building.'),
            errorMessage: z
                .string()
                .optional()
                .describe("Present only when `outcome` is failed: why, as Cardinal Gray's HTTP status and job status or our own reason, truncated to 300 characters. Never Cardinal Gray's response body, which can echo buyer details."),
            previousTransactionId: z
                .string()
                .describe('The Cardinal Gray transaction that may still name the lender.'),
            newTransactionId: z
                .string()
                .optional()
                .describe('Present only when `outcome` is rebuilt: the replacement transaction.'),
            previousPaymentSelection: z
                .string()
                .describe('The payment selection before the change, always native financing.'),
            paymentSelection: z.string().describe('The payment selection the order moved to.'),
        }),
    },
    OEM_PROMO_SCRAPE_COMPLETED: {
        canonical: 'oem_promo_scrape_completed',
        surface: 'backend',
        description: 'A scheduled OEM promotion scrape finished and the promotions it found or changed need review.',
        // Carries no buyer, order or vehicle: this is one aggregate event per
        // scrape run across every OEM, so it attributes to the Ekho ops profile.
        schema: z.object({
            runId: z.string().describe('The fan-in run, so a re-notification is identifiable.'),
            perOemSummary: z
                .string()
                .optional()
                .describe('One Slack-formatted line per OEM with promo activity. Capped before emission with an overflow marker: Segment rejects a track over 32KB outright rather than truncating it, so an uncapped summary would mean the alert never arrives.'),
            totalCreated: z.number().describe('Promotions created across the run.'),
            totalUpdated: z
                .number()
                .describe('Promotions changed across the run, each of which needs re-review.'),
        }),
    },
    // -------------------------------------------------------------------------
    // Financing, payment and order-tracking ops alerts. Same shape as the ops
    // alerts above: no action item behind them, so each is emitted from the call
    // site that handled the moment, after the durable write, in a try/catch that
    // only warns. A track outage must never un-cancel an order or roll back a
    // lender decision.
    //
    // Money rides these as numbers. The Slack templates pre-format it as
    // `$12000.00` strings at the call site; a warehouse column holding a dollar
    // string cannot be summed, so the automation formats it in Liquid instead.
    // -------------------------------------------------------------------------
    LENDER_OFFER_RECEIVED: {
        canonical: 'lender_offer_received',
        surface: 'backend',
        description: "A lender returned an offer on the buyer's financing application and the dealer should reach out.",
        schema: z.object({
            ...opsAlert,
            bankName: z.string().describe('The lender key, for joins. Stable across a rename.'),
            lenderDisplayName: z
                .string()
                .optional()
                .describe('The lender by name, for the ops reader. Absent when the lender document did not resolve; the call site falls back to the key in Slack.'),
        }),
    },
    LENDER_TERMS_REVISED: {
        canonical: 'lender_terms_revised',
        surface: 'backend',
        description: 'A lender counter offer changed the loan amount, down payment or monthly payment on the deal the buyer had already accepted. When signedTermsPreserved is true the order is funded or in kickoff, the alert is the only effect, and the signed numbers were not written.',
        schema: z.object({
            ...opsAlert,
            bankName: z.string().describe('The lender that revised the terms.'),
            oldLoanAmount: z
                .number()
                .describe('Loan amount before the revision. Zero where none was set.'),
            newLoanAmount: z.number().describe('Loan amount after the revision.'),
            oldDownPayment: z.number().describe('Down payment before the revision.'),
            newDownPayment: z.number().describe('Down payment after the revision.'),
            oldMonthlyPayment: z.number().describe('Monthly payment before the revision.'),
            newMonthlyPayment: z.number().describe('Monthly payment after the revision.'),
            signedTermsPreserved: z
                .boolean()
                .optional()
                .describe('True when the signed loan terms and financing contract were left unchanged because the order is funded or in kickoff fulfillment.'),
        }),
    },
    LENDER_APP_WITHDRAWN: {
        canonical: 'lender_app_withdrawn',
        surface: 'backend',
        // The Slack message prints the buyer id as a body line. Here it is the
        // attribution key, so it is not repeated in the payload.
        description: 'A lender moved a financing application to withdrawn status and it needs a human to review it before the rejection is applied.',
        schema: z.object({
            ...opsAlert,
            lenderId: z.string().describe('The lender document id.'),
            lenderName: z
                .string()
                .describe('The lender display name, resolved through `getLenderDisplayName`.'),
        }),
    },
    FINANCING_LENDER_APPLICATION_UPDATED: {
        canonical: 'financing_lender_application_updated',
        surface: 'backend',
        description: 'Ekho marked a lender application on a lender waterfall deal past its deadline, with no decision from that lender. Fires once per lender application, for every lender. The lender can still answer.',
        // The alert names the deal by its order id, so it carries no buyer data.
        schema: z
            .object(opsAlert)
            .omit({ buyerFullName: true })
            .extend({
            status: z
                .literal('PAST_DEADLINE')
                .describe('The status the lender application moved to. Past deadline is the only one sent.'),
            lenderId: z.string().describe('The lender document id, for joins.'),
            lenderName: z
                .string()
                .optional()
                .describe('The lender by name, for the ops reader. Absent when the lender record could not be read.'),
            requiresManualSubmission: z
                .boolean()
                .describe('True when ops submit to this lender by hand: it has no adapter, its adapter does not submit credit apps, its record switches credit app submission off, or the record could not be read. Customer.io alerts ops only when this is true.'),
        }),
    },
    SUBMIT_OCTANE_CREDIT_APPLICATION_MANUALLY: {
        canonical: 'submit_octane_credit_application_manually',
        surface: 'backend',
        description: 'Octane is a relevant lender and a credit-application recipient, so ops has to submit the application to Octane by hand.',
        schema: z.object({
            ...opsAlert,
            ficoScore: z
                .number()
                .optional()
                .describe('The TransUnion FICO score on the credit application. Absent when no report came back, which the Slack message renders as `N/A`.'),
        }),
    },
    APPROVED_WITH_QUALIFYING_STIPS: {
        canonical: 'approved_with_qualifying_stips',
        surface: 'backend',
        description: 'Every non-Octane lender has responded and at least one approved with stipulations, with no stip-free approval anywhere, so ops should submit to Octane.',
        schema: z.object({
            ...opsAlert,
            ficoScore: z
                .number()
                .optional()
                .describe('The TransUnion FICO score, where a report exists.'),
            bankNames: z
                .array(z.string())
                .describe('The lenders that approved with qualifying stipulations. An array rather than the comma-joined string the Slack message renders, so a report can count per lender.'),
            qualifyingStips: z
                .array(z.string())
                .describe('The distinct stipulation keys across those approvals.'),
        }),
    },
    COMPLETE_FRF_USED_ASSET_IN_PORTAL: {
        canonical: 'complete_frf_used_asset_in_portal',
        surface: 'backend',
        description: 'An FRF used-asset submission failed validation, so ops has to finish the asset tab in Dealer Hub by hand.',
        schema: z.object({
            ...opsAlert,
            frfApplicationUrl: z.string().describe('Deep link to the FRF application to finish.'),
            frfApplicationNumber: z
                .string()
                .optional()
                .describe('The QT application number ops searches Dealer Hub by, which is a different identifier from the application id embedded in `frfApplicationUrl`. Absent before FRF issued one.'),
            frfValidationErrors: z
                .array(z.string())
                .describe('What FRF rejected. An array rather than the semicolon-joined string the Slack message renders.'),
            frfProductLine: z.string().describe("FRF's product line for the unit, e.g. Motorcycle."),
            vehicleYear: z.number().optional().describe('Model year, for the asset-tab prefill.'),
            vehicleMake: z.string().optional(),
            vehicleModel: z.string().optional(),
            vehicleSeries: z.string().optional().describe('Absent for a unit with no series.'),
            vehicleRetailValue: z
                .number()
                .optional()
                .describe('Third-party retail value, else the canon retail price, else the base price.'),
            vin: z
                .string()
                .optional()
                .describe('Absent before a VIN is attached, which the Slack message renders as `not attached yet`.'),
        }),
    },
    CONTACT_BUYER_FOR_ACH_FAILURE: {
        canonical: 'contact_buyer_for_ach_failure',
        surface: 'backend',
        description: "Clerq declined to authorize the buyer's ACH payment, so ops has to contact them for another method.",
        schema: z.object({
            ...opsAlert,
            declineReason: z
                .string()
                .optional()
                .describe("Clerq's decline source. Absent where Clerq sent none."),
            declineMessage: z.string().optional().describe("Clerq's decline message, verbatim."),
        }),
    },
    BUYER_DECLINED_ALL_OFFERS_AND_CANCELED: {
        canonical: 'buyer_declined_all_offers_and_canceled',
        surface: 'backend',
        description: 'A dealer-program buyer declined every lender offer and canceled the order, so the lead goes back to the dealer.',
        schema: z.object({ ...opsAlert }),
    },
    NEW_ORDER_PLACED: {
        canonical: 'new_order_placed',
        surface: 'backend',
        description: 'A buyer completed checkout and the order was finalized with its order numbers.',
        schema: z.object({
            ...opsAlert,
            paymentMethod: z
                .string()
                .optional()
                .describe('How the buyer is paying, from the payment selection on the vehicle.'),
            vehicleName: z.string().optional().describe('The vehicle as the buyer sees it named.'),
            inventoryItemId: z
                .string()
                .optional()
                .describe('The stocked unit the order is against. Absent for a warehouse order, which the Slack message renders as `N/A`.'),
        }),
    },
    // -------------------------------------------------------------------------
    // Checkout performance metrics—automated timing measurements emitted from the
    // checkout app, not user interactions.
    // -------------------------------------------------------------------------
    CHECKOUT_CONTENT_LOADED: {
        canonical: 'perf_checkout_content_loaded',
        wireName: 'checkout_content_loaded',
        surface: 'backend',
        description: 'Time-to-content measurement for checkout.',
        schema: z.object({ timeToContentMs: z.number() }),
    },
    CHECKOUT_CONTENT_VISIBLE: {
        canonical: 'perf_checkout_content_visible',
        wireName: 'checkout_content_visible',
        surface: 'backend',
        description: 'Time-to-visible measurement for checkout.',
        schema: z.object({
            timeToContentMs: z.number(),
            timeToVisibleMs: z.number().optional(),
        }),
    },
    // -------------------------------------------------------------------------
    // Booking events. Fired when a lead books or cancels an appointment via
    // the AI Sales Agent. These drive dealer notification emails in
    // Customer.io (T1b-a / T1b-b). Attributed to the assigned rep's
    // platform-user profile (Firebase uid), not the dealer mailbox, because
    // each email can live on only one profile.
    // -------------------------------------------------------------------------
    BOOKING_CREATED: {
        canonical: 'booking_created',
        surface: 'backend',
        description: 'A lead booked an appointment via the AI Sales Agent. Tracked on the assigned sales rep so the T1b-a appointment-booked email in Customer.io sends to that rep.',
        schema: z.object({
            bookingId: z.string().describe('The booking document id.'),
            leadName: z.string().describe('Display name of the lead who booked.'),
            appointmentTime: z
                .string()
                .describe('Human-readable appointment time, formatted in the dealer timezone (e.g. "Mon, Sep 11, 10:00 AM EDT").'),
            bookingType: z
                .string()
                .describe('The type of appointment booked (e.g. "Sales Consultation", "Showroom Visit").'),
            buyerContext: z
                .string()
                .describe('Concise buyer context string from lead enrichment (timeline, budget, payment preference, trade-in).'),
            contactEmail: z
                .string()
                .optional()
                .describe("The lead's email address. Absent when not captured."),
            contactPhone: z
                .string()
                .optional()
                .describe("The lead's phone number, formatted. Absent when not captured."),
            conversationLink: z.string().describe('Admin Portal deep link to the conversation.'),
            eventLink: z
                .string()
                .optional()
                .describe('Link to the calendar event. Absent when no managed calendar is configured for the dealer.'),
            notificationEmail: z
                .string()
                .optional()
                .describe('Mailbox saved on the booked appointment type (skills.appointments.<type>.notificationEmail). Absent when that appointment type has no notification email.'),
            vehicleInterest: z
                .string()
                .describe('Vehicle of interest from lead enrichment, or "Unknown" when not captured.'),
            programOwnerId: z.string().describe('The program owner (dealer group) tenant id.'),
            svpId: z.string().describe('The services provider the booking is for.'),
        }),
    },
    BOOKING_CANCELLED: {
        canonical: 'booking_cancelled',
        surface: 'backend',
        description: 'A booked appointment was cancelled. Tracked on the assigned sales rep so the T1b-b appointment-cancelled email in Customer.io sends to that rep.',
        schema: z.object({
            bookingId: z.string().describe('The booking document id.'),
            leadName: z
                .string()
                .optional()
                .describe('Display name of the lead. Absent when the lead has no name.'),
            appointmentTime: z
                .string()
                .describe('Human-readable appointment time, formatted in the dealer timezone.'),
            notificationEmail: z
                .string()
                .optional()
                .describe('Mailbox saved on the booked appointment type (skills.appointments.<type>.notificationEmail). Absent when that appointment type has no notification email.'),
            programOwnerId: z.string().describe('The program owner (dealer group) tenant id.'),
            svpId: z.string().describe('The services provider the booking was for.'),
        }),
    },
};

export { OPERATIONAL_EVENTS };
//# sourceMappingURL=operational.js.map
